import { memo } from 'react'
import { observer } from 'mobx-react'
import { useInView } from 'react-intersection-observer'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Grid from '@mui/material/Grid'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

import { LoadingMode } from 'components/LoadingProvider'
import HotlineFallback from 'components/HotlineFallback'
import { useErrorState } from 'context/Error/hook'
import CategoryLayout from 'components/Layout/Category'
import { useLoadingMode } from 'stores/utils/hooks/useLoadingMode'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { useDebouncedEffect } from 'hooks/useDebouncedEffect'
import { Button, Theme, Typography } from '@mui/material'
import { NavigateFunction } from 'react-router-dom'
import useEmotionStyles from 'hooks/useEmotionStyles'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { trackTeal } from 'utils/tracking'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'

export const getButtonSizes = (isBackButtonHidden: boolean) => {
  const sizeOfContinueButton = 12
  const sizeOfSecondaryButton = isBackButtonHidden ? 12 : 8
  const sizeOfBackButton = isBackButtonHidden ? 0 : 4

  return {
    sizeOfContinueButton,
    sizeOfSecondaryButton,
    sizeOfBackButton
  }
}

type Props = {
  navigate: NavigateFunction
  continueButtonColor: 'primary' | 'secondary'
  backButtonVariant: 'text' | 'outlined' | 'contained'
  isSmava: boolean
  onBack?: () => void
  onNext: () => void
}

const styles = (theme: Theme) => ({
  flexBasisGrid: {
    flexBasis: 'auto'
  },
  button: {
    maxHeight: '48px',

    '&.Mui-disabled': {
      backgroundColor: `${theme.palette.action.disabledBackground} !important`
    }
  }
})

const PremiumCustomerFooter = observer(
  ({ onNext, continueButtonColor, backButtonVariant, onBack }: Props) => {
    const {
      state: { message },
      hasCooldown
    } = useErrorState()

    const { ref, inView } = useInView()

    const loadingMode = useLoadingMode()
    const {
      rootStore: {
        premiumCustomer,
        traversal: {
          data: {
            system: { advertisementId }
          },
          traversalId
        }
      }
    } = useRootStore()

    useDebouncedEffect(
      () => {
        if (inView) {
          trackInAmplitude(AmplitudeEvent.CtaIsVisible)
        }
      },
      300,
      [inView]
    )

    const { flexBasisGrid, button } = useEmotionStyles(styles, 'primary')
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    const handleSecondaryButtonClick = () => {
      trackTeal({
        transaction: {
          event: 'premium_call_no'
        },
        context: {
          Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
          userAttributes: {
            advertisementId
          }
        }
      })
      premiumCustomer.setPremiumCallRequestStatus(null)
      onNext()
    }

    const handleBackButtonClick = () => {
      onBack?.()
    }

    const handleContinueButtonClick = async () => {
      trackTeal({
        transaction: {
          event: 'premium_call_yes'
        },
        context: {
          Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
          userAttributes: {
            advertisementId
          }
        }
      })
      const { isError } = await premiumCustomer.getIsPremiumCallAccepted(
        advertisementId,
        traversalId
      )
      if (isError) {
        trackTeal({
          transaction: {
            event: 'premium_call_error'
          },
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            userAttributes: {
              advertisementId
            }
          }
        })
      }
      onNext()
    }

    const { sizeOfContinueButton, sizeOfSecondaryButton, sizeOfBackButton } =
      getButtonSizes(false)

    return (
      <CategoryLayout ref={ref}>
        {message && <HotlineFallback message={message} />}
        <Grid
          container
          spacing={1}
          direction={isMobile ? 'column' : 'row-reverse'}
          justifyContent="center"
          alignItems="center"
        >
          <Grid
            className={flexBasisGrid}
            container
            item
            spacing={1}
            direction={isMobile ? 'column' : 'row-reverse'}
            xs={12}
          >
            {/* CONTINUE BUTTON */}
            <Grid item xs={12} sm={sizeOfContinueButton} mb={1}>
              <Button
                className={button}
                size="large"
                fullWidth
                onClick={handleContinueButtonClick}
                id="next-submit"
                name="next-submit"
                variant="contained"
                color={continueButtonColor}
                endIcon={
                  loadingMode === LoadingMode.None ? (
                    <ArrowForwardIcon />
                  ) : undefined
                }
              >
                {'Jetzt Sofortanruf anfordern'}
              </Button>
            </Grid>
            <Typography variant="caption" sx={{ margin: '0 auto', mb: 2 }}>
              Durchschnittliche Wartezeit: 20 bis 60 Sekunden
            </Typography>
            {/* SECONDARY BUTTON */}
            <Grid
              item
              xs={12}
              sm={sizeOfSecondaryButton}
              data-testid="grid-item-next-submit-container"
              display="flex"
              alignItems="center"
              fontWeight={500}
            >
              <Button
                className={button}
                size="large"
                fullWidth
                variant={backButtonVariant}
                color="primary"
                disabled={hasCooldown}
                onClick={handleSecondaryButtonClick}
                endIcon={
                  loadingMode === LoadingMode.None ? (
                    <ArrowForwardIcon />
                  ) : undefined
                }
                sx={{
                  height: '48px',
                  fontSize: 16
                }}
              >
                {'Weiter ohne Sofortanruf'}
              </Button>
            </Grid>

            {/* BACK BUTTON */}

            <Grid item xs={12} sm={sizeOfBackButton}>
              <Button
                className={button}
                size="large"
                onClick={handleBackButtonClick}
                type="button"
                id="back-submit"
                name="back-submit"
                color="primary"
                sx={{
                  height: '48px',
                  fontSize: 16
                }}
                variant={backButtonVariant}
                startIcon={<ArrowBackIcon />}
                fullWidth
              >
                Zurück
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </CategoryLayout>
    )
  }
)

export default memo(PremiumCustomerFooter)
